/**
 * Shared types for the Presence Tracker.
 *
 * Keep this file pure types — no runtime code, no exports of values.
 * The state shape here mirrors the Postgres schema documented in
 * `supabase/01_schema.sql` plus the client-only fields the UI needs.
 */

/** Seven-status enum mirroring the calendar.status check constraint.
 *  A = "Available but not at desk" — added in migration 16. Use this for
 *  someone who's reachable but not at their workstation (e.g. in a meeting,
 *  in the lab, or briefly away from desk). Distinct from R (Remote /
 *  working from home) which means working off-site.
 *  V = "In Rovereto Office" — added in migration 19. Third iCons office
 *  alongside Lodi (default office, code P) and Milan (code M). The letter
 *  is 'V' rather than 'R' because 'R' was already taken by Remote — see
 *  the migration comment for the full reasoning. */
export type StatusCode = "P" | "T" | "R" | "M" | "O" | "A" | "V";

/** ISO date string, "YYYY-MM-DD". */
export type IsoDate = string;

/** Roster entry — one per active or former employee. */
export interface RosterEntry {
  unit: string;
  role: string;
  email: string;
  /** false = former employee. Undefined or true = active. */
  active: boolean;
  /** From migration 07_roster_names_split.sql. Empty for un-backfilled rows. */
  first_name: string;
  last_name: string;
  /** Soft-retention column from migration 12. For former employees only,
   *  this is the date of their last recorded calendar entry. The Company
   *  Overview shows them in months whose first day is ≤ this value,
   *  hides them after. Null for active employees. */
  last_active_day: string | null;
}

/** A user's profiles row. */
export interface ProfileRow {
  id: string;
  email: string;
  /** Linked roster.name. Null = email did not match any roster row. */
  roster_name: string | null;
  /** True for both `admin` and `super_admin` tiers — both see the Dashboard
   *  and can edit other people's roster info. Only super admins can flip
   *  this flag (see migration 10). */
  is_admin: boolean;
  /** Top tier — manages role grants. Implies `is_admin = true`. Only an
   *  existing super admin can promote / demote another super admin. */
  is_super_admin: boolean;
  /** Mirror of whether the user has a password set in auth.users. */
  password_set: boolean;
  /** Receives equipment-booking notification emails (super-admin-chosen,
   *  migration 23). Optional: boot deliberately doesn't select it, so a
   *  client deployed ahead of the migration still signs everyone in. It
   *  is filled by refreshProfilesByRoster (Directory) and realtime. */
  notify_equipment?: boolean;
}

/** Per-person, per-day status map. */
export type CalendarMap = Record<string, Record<IsoDate, StatusCode>>;
export type NoteMap = Record<string, Record<IsoDate, string>>;
/** Office closure days — admin-flagged, company-wide. The value is
 *  the optional label (e.g. "Christmas closure", "Festa della
 *  Repubblica"). Empty string = flagged but no label given. */
export type ClosureMap = Record<IsoDate, string>;
/** Meetings: presence of a (person, day) entry means the person flagged
 *  an in-presence meeting that day. The string is the description (may be
 *  empty if the user just flagged the day without writing a description).
 *  Schema: see migration 09_meeting_description.sql. */
export type MeetingMap = Record<string, Record<IsoDate, string>>;
export type RosterMap = Record<string, RosterEntry>;
export type ProfilesByRoster = Record<string, ProfileRow>;

/** App-wide state. Held in a Preact signal in src/state/store.ts. */
export interface AppState {
  roster: RosterMap;
  calendar: CalendarMap;
  notes: NoteMap;
  meetings: MeetingMap;
  /** Display name of the signed-in user. ADMIN_NAME constant for admin. */
  currentUser: string | null;
  /** True for both admin and super-admin tiers (gates the Dashboard tab,
   *  the act-as picker, and Directory inline editing). */
  isAdmin: boolean;
  /** Strict superset of `isAdmin`. Gates the role-management toggle in
   *  the Directory and a few defensive UI elements. */
  isSuperAdmin: boolean;
  /** Admin-only: whose calendar are they currently editing on behalf of. */
  actAs: string | null;
  /** auth.uid() of the signed-in user. */
  profileId: string | null;
  /** Email pulled from session.user.email — used in the header for admin. */
  adminEmail: string;
  /** Admin-only: keyed by roster_name. */
  profilesByRoster: ProfilesByRoster;
  /** Office closure days flagged by admins — visible to everyone. */
  closures: ClosureMap;
}

/** Sentinel for the synthetic "Administrator" identity. */
export const ADMIN_NAME = "Administrator";

/** Status code → human label. */
export const STATUS_LABEL: Record<StatusCode, string> = {
  P: "In presence",
  T: "Working trip",
  R: "Remote working",
  M: "In Milan office",
  O: "Out of office",
  A: "Available but not at desk",
  V: "In Rovereto Office",
};

/** Active view tab. */
export type ViewKey = "my" | "company" | "dashboard" | "directory" | "equipment";

/** Demo seed file shape (loaded at runtime from /demo-seed.json). */
export interface DemoSeed {
  _meta?: {
    generated_at?: string;
    source?: string;
    people?: number;
    calendar_entries?: number;
    note_entries?: number;
    warning?: string;
  };
  roster: RosterMap;
  calendar: CalendarMap;
  notes: NoteMap;
  meetings: MeetingMap;
}
